/**
 * Portfolio Groups management page.
 *
 * Lists all groups owned by the current user with edit, delete, and duplicate.
 * Each group resolves to a set of offices via hierarchy rules.
 */
import { useState, useEffect, useCallback } from 'react'
import { Plus, Pencil, Trash2, Copy, FolderOpen, Building2, Sparkles } from 'lucide-react'
import { execAPI } from '../services/exec'
import type { PortfolioGroup } from '../types/exec'
import PortfolioGroupEditor from '../components/exec/PortfolioGroupEditor'

/**
 * Starter portfolio templates (Phase G2). Frontend-only — clicking "Use this"
 * clones the rules into a new PortfolioGroup owned by the current user.
 *
 * Codes are USAspending toptier/subtier agency codes. If a leader's actual
 * organizational scope diverges, they can edit the cloned group after creation.
 *
 * Note: there is no agency-level rule type in PortfolioGroupRule, so "All DoW"
 * is approximated by including every DoD sub-agency. A leader who wants pure
 * DoD-wide data can simply NOT scope a dashboard to any portfolio (which
 * defaults to all offices).
 */
interface StarterRule {
  hierarchy_level: 'sub_agency' | 'macom' | 'subcom1' | 'office'
  entity_code: string
  entity_name: string
}
interface Starter {
  name: string
  description: string
  rules: StarterRule[]
}

const STARTER_GROUPS: Starter[] = [
  {
    name: 'All Army',
    description: 'All offices under Department of the Army.',
    rules: [{ hierarchy_level: 'sub_agency', entity_code: '2100', entity_name: 'Department of the Army' }],
  },
  {
    name: 'All Navy',
    description: 'All offices under Department of the Navy.',
    rules: [{ hierarchy_level: 'sub_agency', entity_code: '1700', entity_name: 'Department of the Navy' }],
  },
  {
    name: 'All Air Force',
    description: 'All offices under Department of the Air Force.',
    rules: [{ hierarchy_level: 'sub_agency', entity_code: '5700', entity_name: 'Department of the Air Force' }],
  },
  {
    name: 'All Marine Corps',
    description: 'All offices under U.S. Marine Corps (within Department of the Navy).',
    rules: [{ hierarchy_level: 'macom', entity_code: 'USMC', entity_name: 'U.S. Marine Corps' }],
  },
  {
    name: 'All Space Force',
    description: 'All offices under U.S. Space Force.',
    rules: [{ hierarchy_level: 'macom', entity_code: 'USSF', entity_name: 'U.S. Space Force' }],
  },
]

export default function PortfolioGroups() {
  const [groups, setGroups] = useState<PortfolioGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [editingGroup, setEditingGroup] = useState<PortfolioGroup | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const loadGroups = useCallback(async () => {
    try {
      const data = await execAPI.getPortfolioGroups()
      setGroups(data)
    } catch (err) {
      console.error('Failed to load portfolio groups:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadGroups() }, [loadGroups])

  const handleDelete = async (id: string) => {
    try {
      await execAPI.deletePortfolioGroup(id)
      setDeleteConfirm(null)
      loadGroups()
    } catch (err) {
      console.error('Failed to delete group:', err)
    }
  }

  const handleDuplicate = async (group: PortfolioGroup) => {
    try {
      const newGroup = await execAPI.createPortfolioGroup({
        name: `${group.name} (Copy)`,
        description: group.description,
      })
      // Copy rules
      for (const rule of group.rules) {
        await execAPI.addPortfolioGroupRule(newGroup.id, {
          hierarchy_level: rule.hierarchy_level,
          entity_code: rule.entity_code,
          entity_name: rule.entity_name,
        })
      }
      loadGroups()
    } catch (err) {
      console.error('Failed to duplicate group:', err)
    }
  }

  const handleUseStarter = async (starter: Starter) => {
    try {
      const newGroup = await execAPI.createPortfolioGroup({
        name: starter.name,
        description: starter.description,
      })
      for (const rule of starter.rules) {
        await execAPI.addPortfolioGroupRule(newGroup.id, rule)
      }
      loadGroups()
    } catch (err) {
      console.error('Failed to create starter group:', err)
    }
  }

  const handleEditorClose = () => {
    setEditingGroup(null)
    setShowNew(false)
    loadGroups()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FolderOpen size={20} className="text-gray-500" />
            Portfolio Groups
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Define collections of offices to scope your dashboards and goals.
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} />
          New Group
        </button>
      </div>

      {/* Starter portfolios (Phase G2) — one-click clone */}
      {groups.length === 0 && (
        <div className="mb-6 p-4 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/20">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-blue-600 dark:text-blue-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Start from a template</h3>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            One-click clones a curated portfolio into your account. You can edit the rules afterwards.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {STARTER_GROUPS.map((s) => (
              <button
                key={s.name}
                onClick={() => handleUseStarter(s)}
                className="text-left p-3 rounded-lg border border-blue-100 dark:border-blue-800 bg-white dark:bg-gray-900 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{s.name}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{s.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Group list */}
      {groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <FolderOpen size={36} className="mb-2 opacity-40" />
          <p className="text-xs">Or build a custom group from scratch with the "New Group" button above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <div
              key={group.id}
              className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {group.name}
                  </h3>
                  {group.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{group.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                      <Building2 size={12} />
                      {group.resolved_office_count} offices
                    </span>
                    <span className="text-xs text-gray-400">
                      {group.rules.length} rule{group.rules.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {/* Show rules inline */}
                  {group.rules.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {group.rules.map((rule) => (
                        <span
                          key={rule.id}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                        >
                          {rule.hierarchy_level}: {rule.entity_name || rule.entity_code}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1 ml-3 shrink-0">
                  <button
                    onClick={() => setEditingGroup(group)}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-blue-600"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDuplicate(group)}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"
                    title="Duplicate"
                  >
                    <Copy size={14} />
                  </button>
                  {deleteConfirm === group.id ? (
                    <div className="flex items-center gap-1 text-xs">
                      <button
                        onClick={() => handleDelete(group.id)}
                        className="px-1.5 py-0.5 rounded bg-red-500 text-white hover:bg-red-600"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-1.5 py-0.5 rounded text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(group.id)}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {(showNew || editingGroup) && (
        <PortfolioGroupEditor
          group={editingGroup}
          onClose={handleEditorClose}
        />
      )}
    </div>
  )
}
